import type { LabSettings } from "./settings";

/** What the little phone on an experiment's card shows. */
export interface SketchShape {
  /** Whether the page stops at the bars, reaches under them, or scrolls under them. */
  page: "stops" | "under" | "scrolls";
  tabBar: "full" | "tucked";
  strips: "page" | "dark" | "amber";
  sheet?: "popover" | "amber";
  wash?: "page" | "everywhere";
  ring?: boolean;
}

export interface Experiment {
  id: string;
  title: string;
  summary: string;
  tryThis: string;
  expect: string;
  preset: Partial<LabSettings>;
  sketch: SketchShape;
}

export const experiments = [
  {
    id: "ring",
    title: "Ring of control",
    summary:
      "A blue ring drawn by the page round the very edge of the display, and the green area inside it where a press is sure to land.",
    tryThis:
      "Scroll the list, rotate the phone, and hide and show Safari's toolbar in both orientations. Wherever the ring or the green area is off, drag the sliders until the ring hugs the glass, say what you saw in the note, and send a report.",
    expect:
      "In Safari 26 on a Dynamic Island iPhone, the ring runs round the whole display in both orientations — over the rounded corners, under the clock or the island, under the tab bar — and does not move when the toolbar hides. Anywhere else it is simply the visible window, square-cornered, with nothing to tune. Either way the green interaction safe area sits inside it, clear of the island, the corners, the home indicator and the browser's bars, and the address bar never shrinks while the list scrolls.",
    preset: { scenario: "ring" },
    sketch: { page: "under", tabBar: "full", strips: "page", ring: true },
  },
  {
    id: "today",
    title: "Today's account sheet",
    summary:
      "The app as it ships: the page stops at the bars and the sheet is pinned to the window.",
    tryThis: "Press E in the top right, then Done. Do it a few times.",
    expect:
      "The strips above and below the page go dark a beat after the sheet arrives, and stay dark for a moment after it has gone. That lag is Safari noticing the pinned wash late, both ways.",
    preset: { scenario: "app", frame: "dvh", accountSheet: "edge-panel" },
    sketch: { page: "stops", tabBar: "full", strips: "dark", sheet: "popover", wash: "page" },
  },
  {
    id: "under-the-glass",
    title: "Under the glass",
    summary:
      "Our own sheet, in a page that covers the whole screen but never scrolls. Only the list inside it does.",
    tryThis:
      "Scroll the list to the very bottom and back. Then press E and Done a few times. Open it on a laptop too.",
    expect:
      "The address bar never shrinks. Cards pass under the tab bar's glass all the way to the bottom edge, and “End of the list” rests just above the bar. The wash dims under the clock and battery and under the tab bar, and lifts the moment the sheet closes. On a laptop there is no gap under the list.",
    preset: { scenario: "app", frame: "band", accountSheet: "owned" },
    sketch: { page: "under", tabBar: "full", strips: "page", sheet: "popover", wash: "everywhere" },
  },
  {
    id: "stops-at-lvh",
    title: "Stops at 100lvh",
    summary: "The same app in a frame exactly 100lvh tall.",
    tryThis: "Scroll the list and look closely at the tab bar.",
    expect:
      "Cards reach under the top of the tab bar but not its bottom: 100lvh is the height with the bar tucked away, so the frame ends where the small pill would start and the rest is body colour. This is why “Under the glass” runs past it.",
    preset: { scenario: "app", frame: "lvh", accountSheet: "owned" },
    sketch: { page: "under", tabBar: "full", strips: "page" },
  },
  {
    id: "under-the-glass-pinned",
    title: "Under the glass, pinned",
    summary: "The same app in a position: fixed frame instead of one in the page.",
    tryThis: "Scroll the list, then read “under the tab bar” in the readout.",
    expect:
      "If Safari clips fixed elements to the visible part of the screen, the cards stop at the tab bar instead of passing under it, and the readout says 0. If they do pass under, pinned frames are an option after all.",
    preset: { scenario: "app", frame: "fixed", accountSheet: "owned" },
    sketch: { page: "under", tabBar: "full", strips: "page", sheet: "popover", wash: "everywhere" },
  },
  {
    id: "website",
    title: "An ordinary website",
    summary: "A document three screens tall that scrolls like any web page.",
    tryThis: "Scroll down slowly, then back up. Watch the readout.",
    expect:
      "The tab bar tucks into a small pill on the way down and comes back on the way up. The coloured bands and their numbers show through both bars, right to the bottom edge. 100dvh and visualViewport.height change as the bar moves; 100lvh and 100svh never do.",
    preset: { scenario: "bench", height: "long" },
    sketch: { page: "scrolls", tabBar: "tucked", strips: "page" },
  },
  {
    id: "edge-colour",
    title: "Edge colour",
    summary: "Where Safari gets the colour of the strips from.",
    tryThis:
      "Switch the bottom sheet between fixed and in-page, and the body colour between its three.",
    expect:
      "With the sheet fixed, the strip under the page turns amber a beat after the sheet appears. With it in the page, the strip is the body colour, whichever you pick.",
    preset: { scenario: "bench", height: "dvh", sheet: "fixed", body: "rose" },
    sketch: { page: "stops", tabBar: "full", strips: "amber", sheet: "amber" },
  },
  {
    id: "fixed-wash",
    title: "A fixed wash",
    summary: "A full-screen dimming layer pinned to the window, like every dialog in @pax/ui.",
    tryThis: "Close the dialog, then turn “fixed wash” back on.",
    expect:
      "The page dims but the strips above and below it do not — or they darken a beat late and hold the colour briefly after the wash is gone.",
    preset: { scenario: "bench", height: "dvh", scrim: true },
    sketch: { page: "stops", tabBar: "full", strips: "dark", wash: "page" },
  },
  {
    id: "bench",
    title: "Free bench",
    summary: "Every setting, nothing chosen for you.",
    tryThis: "Combine document height, viewport-fit, overscroll and touch-action.",
    expect: "Whatever you find. Leave the settings in the address and send the link to someone.",
    preset: { scenario: "bench" },
    sketch: { page: "stops", tabBar: "full", strips: "page" },
  },
] as const satisfies readonly Experiment[];

export type ExperimentId = (typeof experiments)[number]["id"];

export function findExperiment(id: unknown): Experiment | undefined {
  return experiments.find(experiment => experiment.id === id);
}
